<template>
  <div class="newsflash-ja-page">
    <div class="newsflash-ja-container">
      <header class="ja-header">
        <div>
          <h1>Revision de noticias flash</h1>
          <p>Revisa las noticias pendientes y publica la que corresponda.</p>
        </div>
      </header>

      <section v-if="loading" class="state-panel">
        <div class="loading-spinner"></div>
        <h2>Cargando revision</h2>
        <p>Recuperando noticias pendientes.</p>
      </section>

      <section v-else-if="errorMessage" class="state-panel">
        <span class="material-icons-outlined state-icon">lock</span>
        <h2>No se puede acceder</h2>
        <p>{{ errorMessage }}</p>
        <button type="button" class="primary-button" @click="loadBatch">Reintentar</button>
      </section>

      <section v-else-if="completedAction" class="state-panel feedback-panel">
        <span class="material-icons-outlined state-icon success-icon">{{ completedIcon }}</span>
        <h2>{{ completedTitle }}</h2>
        <router-link v-if="publishedRoute" class="primary-button" :to="publishedRoute">
          <span class="material-icons-outlined">open_in_new</span>
          Abrir noticia publicada
        </router-link>
      </section>

      <section v-else-if="!items.length && publishedRoute" class="state-panel">
        <span class="material-icons-outlined state-icon success-icon">task_alt</span>
        <h2>La noticia flash ya esta publicada.</h2>
        <router-link class="primary-button" :to="publishedRoute">
          <span class="material-icons-outlined">open_in_new</span>
          Abrir noticia publicada
        </router-link>
      </section>

      <section v-else-if="!items.length" class="state-panel">
        <span class="material-icons-outlined state-icon">task_alt</span>
        <h2>Esta revision ya no tiene noticias disponibles.</h2>
      </section>

      <section v-else class="batch-layout">
        <div class="review-list">
          <article
            v-for="item in items"
            :key="item.id"
            class="review-card"
          >
            <div class="review-content">
              <div class="review-topline">
                <span class="status-chip">{{ item.status_label }}</span>
                <span class="volatility-pill">{{ formatVolatility(item.volatility_percent) }}</span>
                <span class="type-pill">{{ item.news_type_label }}</span>
              </div>

              <h2>{{ item.title }}</h2>

              <div class="review-meta">
                <span>{{ item.media }}</span>
                <span>{{ formatDate(item.publication_date) }}</span>
              </div>

              <article class="content-block">
                <h3>Que ha pasado</h3>
                <p>{{ item.what_happened || item.summary }}</p>
              </article>

              <article v-if="item.economic_consequences" class="content-block">
                <h3>Repercusion en el ambito economico</h3>
                <p>{{ item.economic_consequences }}</p>
              </article>

              <article class="content-block internal-block">
                <h3>Analisis de volatilidad</h3>
                <div class="internal-grid">
                  <div>
                    <span>Volatilidad estimada</span>
                    <strong>{{ formatVolatility(item.volatility_percent) }}</strong>
                  </div>
                  <div>
                    <span>Ambito</span>
                    <strong>{{ item.news_type_label }}</strong>
                  </div>
                  <div v-if="item.sector">
                    <span>Sector</span>
                    <strong>{{ item.sector }}</strong>
                  </div>
                  <div v-if="item.historical_high_ticker">
                    <span>Ticker cerca de extremo historico</span>
                    <strong>{{ formatHistoricalHighTicker(item.historical_high_ticker) }}</strong>
                  </div>
                </div>
                <div v-if="item.comparable_event" class="comparable-event">
                  <span>Suceso comparable</span>
                  <strong>{{ item.comparable_event.fecha }} · {{ item.comparable_event.activo_o_mercado_afectado }}</strong>
                  <p>{{ item.comparable_event.hecho }}</p>
                  <p>{{ item.comparable_event.movimiento_observado }}</p>
                  <a v-if="item.comparable_event.fuente_url" :href="item.comparable_event.fuente_url" target="_blank" rel="noopener">
                    Fuente del suceso
                  </a>
                </div>
                <p v-if="item.volatility_reason">{{ item.volatility_reason }}</p>
              </article>

              <div v-if="item.affected_stocks_count" class="stocks-list">
                <div v-for="stock in item.affected_stocks" :key="`${item.id}-${stock.ticker}`" class="stock-row">
                  <strong>{{ stock.ticker }}</strong>
                  <span>{{ stock.nombre_empresa }}</span>
                </div>
              </div>

              <div class="card-actions">
                <button type="button" class="primary-button" :disabled="actionLoading" @click="publishItem(item)">
                  <span class="material-icons-outlined">publish</span>
                  Publicar esta noticia
                </button>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import newsflashAiApi from '@/services/newsflashAiApi';

export default {
  name: 'NewsflashJaBatchReview',
  data() {
    return {
      items: [],
      publishedItems: [],
      loading: false,
      actionLoading: false,
      errorMessage: '',
      completedAction: '',
    };
  },
  computed: {
    batchIds() {
      const raw = this.$route.query.ids;
      const value = Array.isArray(raw) ? raw.join(',') : String(raw || '');
      const ids = value
        .split(',')
        .map(id => Number(id.trim()))
        .filter(id => id > 0 && Math.floor(id) === id);

      return [...new Set(ids)];
    },
    completedIcon() {
      return this.completedAction === 'approved' ? 'check_circle' : 'block';
    },
    completedTitle() {
      return 'Noticia flash publicada.';
    },
    publishedRoute() {
      const item = this.publishedItems.length ? this.publishedItems[0] : null;
      if (!item || !item.slug) {
        return null;
      }

      const key = newsflashAiApi.getAccessKey();
      return {
        name: 'NewsflashAdminJaPublished',
        params: {
          slug: item.slug,
        },
        query: key ? { k: key } : {},
      };
    },
  },
  watch: {
    '$route.fullPath'() {
      this.captureAccessKey();
      this.loadBatch();
    },
  },
  mounted() {
    document.body.classList.add('newsflash-ja-review-active');
    this.captureAccessKey();
    this.loadBatch();
  },
  beforeDestroy() {
    document.body.classList.remove('newsflash-ja-review-active');
  },
  methods: {
    captureAccessKey() {
      const key = this.$route.query.k;
      if (!key) {
        return;
      }

      newsflashAiApi.storeAccessKey(key);
      const query = { ...this.$route.query };
      delete query.k;
      this.$router.replace({ path: this.$route.path, query }).catch(() => {});
    },
    async loadBatch() {
      this.loading = true;
      this.errorMessage = '';
      this.completedAction = '';
      this.publishedItems = [];

      if (!this.batchIds.length) {
        this.items = [];
        this.errorMessage = 'El enlace de revision no contiene noticias validas.';
        this.loading = false;
        return;
      }

      try {
        const response = await newsflashAiApi.getJaBatch(this.batchIds);
        this.items = response.data && Array.isArray(response.data.items) ? response.data.items : [];
        this.publishedItems = response.data && Array.isArray(response.data.published) ? response.data.published : [];
      }
      catch (error) {
        this.items = [];
        this.errorMessage = this.resolveErrorMessage(error);
      }
      finally {
        this.loading = false;
      }
    },
    async publishItem(item) {
      if (!item || !item.id) {
        return;
      }

      if (!window.confirm('Publicar esta noticia y descartar el resto?')) {
        return;
      }

      this.actionLoading = true;
      this.errorMessage = '';

      try {
        const response = await newsflashAiApi.approveJaBatch([item.id], this.batchIds);
        this.publishedItems = response.data && Array.isArray(response.data.published) ? response.data.published : [];
        this.items = [];
        this.completedAction = 'approved';
      }
      catch (error) {
        this.errorMessage = this.resolveErrorMessage(error);
      }
      finally {
        this.actionLoading = false;
      }
    },
    formatDate(value) {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return value || '-';
      }

      return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    },
    formatVolatility(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) {
        return '-';
      }

      return `${new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(number)}%`;
    },
    formatHistoricalHighTicker(ticker) {
      if (!ticker) {
        return '-';
      }

      const extreme = ticker.tipo_extremo_label || 'Extremo historico';
      const days = Number.isFinite(Number(ticker.dias_desde_extremo)) ? `, hace ${ticker.dias_desde_extremo} dias` : '';
      const date = ticker.fecha_extremo_historico ? `, ${ticker.fecha_extremo_historico}` : '';
      return `${ticker.ticker} - ${ticker.nombre_empresa} (${extreme}${days}${date})`;
    },
    resolveErrorMessage(error) {
      const response = error && error.response ? error.response : null;
      if (response && response.data && response.data.message) {
        return response.data.message;
      }

      if (response && response.status === 403) {
        return 'No tienes permisos o la clave temporal ha caducado.';
      }

      if (response && response.status === 404) {
        return 'La noticia no existe o ya no esta disponible.';
      }

      return 'No se pudo completar la operacion.';
    },
  },
  metaInfo() {
    return {
      title: 'Revision JA noticias flash | Eurekers',
    };
  },
};
</script>

<style lang="scss" scoped>
.newsflash-ja-page {
  min-height: 100vh;
  background: var(--color-background);
  color: var(--color-text-primary);
}

.newsflash-ja-container {
  max-width: 1040px;
  margin: 0 auto;
  padding: 24px;
}

.ja-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 18px;
  margin-bottom: 22px;

  h1 {
    margin: 0 0 8px;
    color: var(--color-primary);
    font-size: 30px;
    font-weight: 600;
  }

  p {
    margin: 0;
    color: var(--color-text-secondary);
  }
}

.primary-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 9px 14px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  background: var(--color-highlight);
  color: var(--color-text-on-highlight);
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.state-panel,
.review-card {
  background: var(--color-surface);
  border: 1px solid var(--color-outline);
  border-radius: 8px;
}

.state-panel {
  display: flex;
  min-height: 260px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 28px;
  text-align: center;

  h2,
  p {
    margin: 0;
  }
}

.feedback-panel {
  align-items: flex-start;
  text-align: left;
}

.loading-spinner {
  width: 34px;
  height: 34px;
  border: 3px solid var(--color-outline);
  border-top-color: var(--color-highlight);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.state-icon {
  color: var(--color-error);
  font-size: 34px;
}

.success-icon {
  color: var(--color-highlight);
}

.review-list {
  display: grid;
  gap: 12px;
}

.review-card {
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.review-content {
  display: grid;
  gap: 12px;
  padding: 18px;

  h2 {
    margin: 0;
    color: var(--color-primary);
    font-size: 20px;
    line-height: 1.3;
  }
}

.card-actions {
  display: flex;
  justify-content: flex-end;
}

.review-topline,
.review-meta,
.stocks-list,
.stock-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.status-chip,
.volatility-pill,
.type-pill {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-chip {
  background: var(--color-active-surface);
  color: var(--color-text-primary);
}

.volatility-pill {
  background: rgba(0, 173, 217, 0.12);
  color: var(--color-highlight);
}

.type-pill {
  background: var(--color-background);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-outline);
}

.review-meta {
  color: var(--color-text-secondary);
  font-size: 13px;
}

.content-block {
  padding: 14px;
  background: var(--color-background);
  border: 1px solid var(--color-outline);
  border-radius: 8px;

  h3 {
    margin: 0 0 8px;
    color: var(--color-primary);
    font-size: 15px;
  }

  p {
    margin: 0;
    color: var(--color-text-secondary);
    line-height: 1.6;
    white-space: pre-line;
  }
}

.internal-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;

  div {
    padding: 10px;
    background: var(--color-surface);
    border: 1px solid var(--color-outline);
    border-radius: 8px;
  }

  span {
    display: block;
    margin-bottom: 4px;
    color: var(--color-text-secondary);
    font-size: 12px;
  }
}

.comparable-event {
  display: grid;
  gap: 6px;
  padding: 12px;
  margin-bottom: 12px;
  background: var(--color-surface);
  border: 1px solid var(--color-outline);
  border-radius: 8px;

  span {
    color: var(--color-text-secondary);
    font-size: 12px;
  }

  p {
    margin: 0;
  }

  a {
    color: var(--color-highlight);
    font-weight: 700;
    text-decoration: none;
  }
}

.stock-row {
  padding: 8px 10px;
  background: var(--color-background);
  border: 1px solid var(--color-outline);
  border-radius: 8px;

  strong {
    color: var(--color-primary);
  }

  span {
    color: var(--color-text-secondary);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 820px) {
  .ja-header {
    grid-template-columns: 1fr;
    align-items: start;
  }
}

@media (max-width: 560px) {
  .newsflash-ja-container {
    padding: 18px 14px;
  }

  .review-content {
    padding: 16px 14px;
  }

  .card-actions,
  .card-actions button {
    width: 100%;
  }

  .internal-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<style lang="scss">
body.newsflash-ja-review-active #launcher {
  display: none;
}
</style>
